import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '../store/auth.store';
import { ROUTES } from '../routes';
import { waitForAuthHydration } from './wait-for-auth-hydration';

/**
 * Angular equivalent of Consumer's app/routes.tsx `RootRedirect` — decides
 * where `/` (and any unmatched path) should land based on auth state:
 * Dashboard if already signed in, otherwise straight into the new
 * phone-number login flow (`/login`).
 *
 * NOTE: unauthenticated users are intentionally routed directly to the new
 * login flow and no longer through Splash — Splash still exists and is
 * reachable at `/splash`, it's just not the default entry point anymore while
 * the new login flow is being defined. (Splash's eligibility check + identity
 * pre-cache that the legacy `/login` relied on are therefore NOT run on this
 * path; the new flow will handle its own setup.)
 *
 * Critically, this *waits* for the bootstrap hydration kicked off in
 * `app.ts`'s `ngOnInit` to finish (`AuthStore.isLoading()` mirrors Redux's
 * `auth.isLoading`, which Consumer's `RootRedirect` also awaits before
 * deciding) rather than deciding immediately against the default
 * `isAuthenticated: false` state. Without this wait, a returning signed-in
 * user would flash Splash before anything corrected course to Dashboard.
 * See `wait-for-auth-hydration.ts` — the same wait is now also applied to
 * `protectedGuard`/`guestGuard`/`onboardingGuard`, which were missing it.
 */
export const rootRedirectGuard: CanActivateFn = async () => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  await waitForAuthHydration(authStore);

  return router.createUrlTree([`/${authStore.isAuthenticated() ? ROUTES.DASHBOARD : ROUTES.LOGIN}`]);
};
